import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { TeamNewFinStatusReasonTagID } from '../../../../../../../main/teams/teamsTypes';

import {
  UniversalFilterCompanyNewFinTagsValue,
  UniversalFilterCompanyNewFinTagsModes,
  UniversalFilterCompanyNewFinTagsFilterState,
  UniversalFilterCompanyNewFinTagsOnFilterStateChange,
  UniversalFilterCompanyNewFinTagsOnChange
} from '../../UniversalFilterCompanyNewFinTags.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterCompanyNewFinTagsFilterState } from '../useUniversalFilterCompanyNewFinTagsFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterCompanyNewFinTagsModes.IS_ANY_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterCompanyNewFinTagsModes>[] =
  [
    {
      value: UniversalFilterCompanyNewFinTagsModes.IS_ANY_OF,
      i18nText: filtersKeys.modes.isAnyOf
    },
    {
      value: UniversalFilterCompanyNewFinTagsModes.IS_NONE_OF,
      i18nText: filtersKeys.modes.isNoneOf
    }
  ];

interface UniversalFilterCompanyNewFinTagsOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterCompanyNewFinTagsValue;
  onChange: UniversalFilterCompanyNewFinTagsOnChange;
}

function useUniversalFilterCompanyNewFinTags({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterCompanyNewFinTagsOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterCompanyNewFinTagsFilterState>(
      filterStateCacheKey
    );

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterCompanyNewFinTagsFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterCompanyNewFinTagsFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          tagIds: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterCompanyNewFinTagsOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterCompanyNewFinTagsModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        tagIds: filterState.tagIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleTagIdsChange = useCallback<
    (tagIds: TeamNewFinStatusReasonTagID[]) => void
  >(
    (tagIds) => {
      handleFilterStateChange({
        mode: filterState.mode,
        tagIds: tagIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleTagIdsChange
  };
}

export default useUniversalFilterCompanyNewFinTags;
